import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router';

import { Box, Button, MenuItem, TextField } from '@mui/material';

import Spinner from '~/components/Common/Spinner';
import { BundleItem, BundleItemType } from '~/components/Cruises/pages/Bundles/types';

import { useCreateBundleItem } from '~/hooks/apis/cruises/useBundleItemMutations';

const ITEM_TYPES: Array<{ value: BundleItemType; label: string }> = [
  { value: 'PRE_ACCOMMODATION', label: 'Pre Accommodation' },
  { value: 'POST_ACCOMMODATION', label: 'Post Accommodation' },
];

type FormValues = {
  itemType: BundleItemType;
  displayName: string;
};

const INITIAL_FORM_STATE: FormValues = {
  itemType: 'PRE_ACCOMMODATION',
  displayName: '',
};

type Props = {
  bundleId: string;
  bundleItem?: BundleItem;
};

function BundleItemForm({ bundleId, bundleItem }: Props) {
  const history = useHistory();
  const [formValues, setFormValues] = useState<FormValues>(INITIAL_FORM_STATE);
  const { enqueueSnackbar } = useSnackbar();

  const createMutation = useCreateBundleItem();

  const handleChangeField = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name) {
      setFormValues((prev) => ({ ...prev, [name]: value ?? undefined }));
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    createMutation.mutate(
      { bundleId, data: formValues },
      {
        onSuccess: () => {
          enqueueSnackbar('Bundle item saved successfully', { variant: 'success' });
          history.push(`/cruises/bundles/edit/${bundleId}/items`);
        },
        onError: () => {
          enqueueSnackbar('Error saving bundle item', { variant: 'error' });
        },
      },
    );
  }, [formValues, history, enqueueSnackbar, bundleId, createMutation]);

  const handleCancel = useCallback(() => {
    history.push(`/cruises/bundles/edit/${bundleId}/items`);
  }, [history, bundleId]);

  const isSaveEnabled = useMemo(() => {
    return !createMutation.isPending && formValues.itemType && formValues.displayName.trim().length > 0;
  }, [formValues, createMutation.isPending]);

  useEffect(() => {
    if (bundleItem) {
      setFormValues({
        itemType: bundleItem.itemType,
        displayName: bundleItem.displayName,
      });
    }
  }, [bundleItem]);

  return (
    <Box>
      <Box mt={2}>
        <TextField
          select
          fullWidth
          name="itemType"
          label="Item Type"
          value={formValues.itemType}
          onChange={handleChangeField}
        >
          {ITEM_TYPES.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box mt={2}>
        <TextField
          required
          fullWidth
          name="displayName"
          label="Display Name"
          onChange={handleChangeField}
          inputProps={{ maxLength: 100 }}
          value={formValues.displayName}
          helperText="e.g. Pre-Cruise Hotel Stay"
        />
      </Box>

      <Box mt={3} gap={1} display="flex" justifyContent="end">
        <Button variant="outlined" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!isSaveEnabled}>
          {createMutation.isPending && <Spinner size={15} />}
          Save Changes
        </Button>
      </Box>
    </Box>
  );
}

export default BundleItemForm;
